/* CapTablePanel — Micro CFO Module 2 / Session B-1.
 *
 * Manual-entry cap table workspace mounted into MicroCFOWorkspace.
 * 4 sub-tabs (Share Classes · Shareholders · Holdings · Allotments) plus
 * an ESOP Pool block. All CRUD goes through:
 *
 *   GET    /api/clients/:id/shareholding
 *   POST   /api/clients/:id/shareholding/{collection}
 *   PATCH  /api/clients/:id/shareholding/{collection}/{item_id}
 *   DELETE /api/clients/:id/shareholding/{collection}/{item_id}
 *   PUT    /api/clients/:id/shareholding/esop-pool
 */
import { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Plus, Trash2, Loader2, AlertCircle, RefreshCcw, Save, X,
  Download, Sparkles, Clock, CheckCircle2,
} from 'lucide-react';
import { CollapsibleSection } from '@/pages/admin/ccfs/components/CollapsibleSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

const SHARE_CLASS_KINDS = [
  { value: 'equity',     label: 'Equity' },
  { value: 'preference', label: 'Preference' },
  { value: 'debenture',  label: 'Debenture' },
  { value: 'warrant',    label: 'Warrant' },
  { value: 'other',      label: 'Other' },
];
const SHARE_CLASS_SUB_KINDS = {
  preference: [
    { value: 'ccps', label: 'CCPS — Compulsorily Convertible' },
    { value: 'ocps', label: 'OCPS — Optionally Convertible' },
    { value: 'ncps', label: 'NCPS — Non-Convertible' },
  ],
  debenture: [
    { value: 'ccd', label: 'CCD — Compulsorily Convertible' },
    { value: 'ocd', label: 'OCD — Optionally Convertible' },
    { value: 'ncd', label: 'NCD — Non-Convertible' },
  ],
};
const NAME_PRESETS_BY_KIND = {
  equity:     ['Equity Class A', 'Equity Class B', 'Sweat Equity', 'ESOP Equity', 'Other Than Cash'],
  preference: ['Series Seed Preference', 'Series A Preference'],
  debenture:  ['Series A Debenture', 'Convertible Note'],
  warrant:    ['Series A Warrant'],
  other:      [],
};
const SHAREHOLDER_KINDS = [
  { value: 'founder',  label: 'Founder' },
  { value: 'employee', label: 'Employee' },
  { value: 'investor', label: 'Investor' },
  { value: 'family',   label: 'Family' },
  { value: 'other',    label: 'Other' },
];
const CONSIDERATION_TYPES = [
  { value: 'cash',           label: 'Cash' },
  { value: 'other_than_cash',label: 'Other than Cash' },
  { value: 'bonus',          label: 'Bonus' },
  { value: 'conversion',     label: 'Conversion' },
  { value: 'esop_exercise',  label: 'ESOP Exercise' },
];

const API = process.env.REACT_APP_BACKEND_URL;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('ratnatraya_token')}` };
}

async function apiCall(method, path, body) {
  const res = await fetch(`${API}/api/clients/${path}`, {
    method,
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `${method} ${path} failed`);
  return data;
}

function fmtNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN');
}

// ─────────────────────── SHARE CLASSES ───────────────────────
function ShareClassesTab({ data, clientId, refresh }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', kind: 'equity', sub_kind: '', par_value: '', rights: '', conversion_ratio: '' });
  const reset = () => setForm({ name: '', kind: 'equity', sub_kind: '', par_value: '', rights: '', conversion_ratio: '' });

  const subKindOptions = SHARE_CLASS_SUB_KINDS[form.kind] || [];
  const needsSubKind = subKindOptions.length > 0;
  const needsConvRatio = form.kind === 'warrant'
    || (form.kind === 'preference' && ['ccps', 'ocps'].includes(form.sub_kind))
    || (form.kind === 'debenture'  && ['ccd', 'ocd'].includes(form.sub_kind));

  const namePresets = NAME_PRESETS_BY_KIND[form.kind] || [];

  const submit = async () => {
    setSaving(true);
    try {
      await apiCall('POST', `${clientId}/shareholding/share-classes`, {
        name: form.name,
        kind: form.kind,
        sub_kind: form.sub_kind || null,
        par_value: form.par_value ? parseFloat(form.par_value) : null,
        rights: form.rights || '',
        conversion_ratio: form.conversion_ratio || '',
      });
      toast.success('Share class added');
      reset();
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this share class?')) return;
    try {
      await apiCall('DELETE', `${clientId}/shareholding/share-classes/${id}`);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="cap-share-classes-tab">
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-share-class-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Share Class
        </Button>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-3">
          {/* Row 1 — Primary kind FIRST, per user spec */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Kind of instrument</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, sub_kind: '' })}>
                <SelectTrigger data-testid="sc-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHARE_CLASS_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {needsSubKind && (
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Convertibility</label>
                <Select value={form.sub_kind} onValueChange={(v) => setForm({ ...form, sub_kind: v })}>
                  <SelectTrigger data-testid="sc-subkind-select">
                    <SelectValue placeholder="Select convertibility" />
                  </SelectTrigger>
                  <SelectContent>
                    {subKindOptions.map((k) => (
                      <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Row 2 — Name with presets */}
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Class name</label>
            <Input
              placeholder='e.g. "Class A" · "Sweat Equity" · "Series A Preference"'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="sc-name-input"
            />
            {namePresets.length > 0 && (
              <div className="flex gap-1 flex-wrap mt-1.5" data-testid="sc-name-presets">
                {namePresets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm({ ...form, name: p })}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 text-slate-600"
                    data-testid={`sc-name-preset-${p.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Row 3 — Par value, conv ratio, rights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="Par value (₹)" type="number" value={form.par_value}
              onChange={(e) => setForm({ ...form, par_value: e.target.value })}
              data-testid="sc-par-input" />
            {needsConvRatio && (
              <Input placeholder='Conversion ratio (e.g. "1:10")' value={form.conversion_ratio}
                onChange={(e) => setForm({ ...form, conversion_ratio: e.target.value })}
                data-testid="sc-convratio-input" />
            )}
            <Input placeholder="Rights summary" value={form.rights}
              onChange={(e) => setForm({ ...form, rights: e.target.value })}
              className={needsConvRatio ? '' : 'sm:col-span-2'}
              data-testid="sc-rights-input" />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { reset(); setOpen(false); }}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={submit}
              disabled={saving || !form.name.trim() || (needsSubKind && !form.sub_kind)}
              data-testid="sc-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        </div>
      )}
      {(data.share_classes || []).length === 0 ? (
        <p className="text-xs text-slate-400 italic">No share classes yet.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-right px-3 py-2">Par Value</th>
                <th className="text-left px-3 py-2">Conv. Ratio</th>
                <th className="text-left px-3 py-2">Rights</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.share_classes.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`sc-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{c.kind}</span>
                    {c.sub_kind && (
                      <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 ml-1">{c.sub_kind}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{c.par_value ? `₹${fmtNum(c.par_value)}` : '—'}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono">{c.conversion_ratio || (c.kind === 'equity' ? '—' : '1:1')}</td>
                  <td className="px-3 py-2 text-slate-600">{c.rights || '—'}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => remove(c.id)} className="text-rose-500 hover:text-rose-700" data-testid={`sc-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── SHAREHOLDERS ───────────────────────
function ShareholdersTab({ data, mcaDirectors, clientId, refresh }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const initForm = { name: '', kind: 'founder', pan: '', din: '', folio_number: '',
                     email: '', is_resident: true };
  const [form, setForm] = useState(initForm);
  const reset = () => setForm(initForm);

  // Directors already added as shareholders — hide from picker to avoid dupes
  const existingDins = new Set((data.shareholders || []).map((s) => (s.din || '').trim()).filter(Boolean));
  const availableDirectors = (mcaDirectors || []).filter((d) => d.din && !existingDins.has(d.din));

  const populateFromDirector = (dinValue) => {
    const dir = (mcaDirectors || []).find((d) => d.din === dinValue);
    if (!dir) return;
    setForm({
      ...form,
      name: dir.name || form.name,
      din: dir.din || '',
      pan: dir.pan || form.pan,
      kind: 'founder',
    });
    toast.success(`Populated from director: ${dir.name}`);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await apiCall('POST', `${clientId}/shareholding/shareholders`, form);
      toast.success('Shareholder added');
      reset(); setOpen(false); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this shareholder?')) return;
    try {
      await apiCall('DELETE', `${clientId}/shareholding/shareholders/${id}`);
      toast.success('Deleted'); refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="cap-shareholders-tab">
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-shareholder-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Shareholder
        </Button>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-3">
          {/* Populate-from-director helper */}
          {availableDirectors.length > 0 && (
            <div className="p-2 border border-indigo-100 bg-indigo-50/60 rounded" data-testid="director-picker-box">
              <label className="text-[10px] font-bold uppercase text-indigo-700">
                Populate from MCA director (optional)
              </label>
              <Select onValueChange={populateFromDirector}>
                <SelectTrigger data-testid="director-picker-select" className="mt-1">
                  <SelectValue placeholder={`${availableDirectors.length} MCA director(s) available`} />
                </SelectTrigger>
                <SelectContent>
                  {availableDirectors.map((d) => (
                    <SelectItem key={d.din} value={d.din} data-testid={`director-picker-option-${d.din}`}>
                      {d.name} · DIN {d.din} · {d.designation || 'Director'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="Full name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sh-name-input" />
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="sh-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SHAREHOLDER_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sh-email-input" />
            <Input placeholder="PAN" value={form.pan}
              onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="sh-pan-input" />
            <Input placeholder="DIN (if director)" value={form.din}
              onChange={(e) => setForm({ ...form, din: e.target.value })} data-testid="sh-din-input" />
            <Input placeholder="Folio number" value={form.folio_number}
              onChange={(e) => setForm({ ...form, folio_number: e.target.value })} data-testid="sh-folio-input" />
            <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-3">
              <input type="checkbox" checked={form.is_resident}
                onChange={(e) => setForm({ ...form, is_resident: e.target.checked })}
                data-testid="sh-resident-checkbox" />
              Indian resident
            </label>
          </div>
          {/* FEMA compliance nudge */}
          {!form.is_resident && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5" data-testid="fema-nudge">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>Non-resident shareholder — FEMA compliance may apply.</strong>{' '}
                Track FC-GPR filing (within 30 days of allotment), FDI reporting,
                and downstream investment disclosures. This shareholder will be
                flagged on the Cap Table for you to keep an eye on.
              </span>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { reset(); setOpen(false); }}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={saving || !form.name.trim()} data-testid="sh-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        </div>
      )}
      {(data.shareholders || []).length === 0 ? (
        <p className="text-xs text-slate-400 italic">No shareholders yet.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">PAN</th>
                <th className="text-left px-3 py-2">DIN</th>
                <th className="text-left px-3 py-2">Folio No.</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Compliance</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.shareholders.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`sh-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                  <td className="px-3 py-2"><span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">{s.kind}</span></td>
                  <td className="px-3 py-2 font-mono text-slate-600">{s.pan || '—'}</td>
                  <td className="px-3 py-2 font-mono text-slate-600">{s.din || '—'}</td>
                  <td className="px-3 py-2 font-mono text-slate-600">{s.folio_number || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{s.email || '—'}</td>
                  <td className="px-3 py-2">
                    {!s.is_resident ? (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`sh-fema-flag-${s.id}`}>
                        NRI · FC-GPR?
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400">Resident</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => remove(s.id)} className="text-rose-500 hover:text-rose-700" data-testid={`sh-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── HOLDINGS ───────────────────────
function HoldingsTab({ data, clientId, refresh }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const init = { shareholder_id: '', share_class_id: '', shares: '', amount_paid: '', as_of_date: '', certificate_no: '' };
  const [form, setForm] = useState(init);

  const submit = async () => {
    setSaving(true);
    try {
      await apiCall('POST', `${clientId}/shareholding/holdings`, {
        shareholder_id: form.shareholder_id,
        share_class_id: form.share_class_id,
        shares: parseFloat(form.shares),
        amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : 0,
        as_of_date: form.as_of_date || null,
        certificate_no: form.certificate_no || '',
      });
      toast.success('Holding added');
      setForm(init); setOpen(false); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this holding?')) return;
    try {
      await apiCall('DELETE', `${clientId}/shareholding/holdings/${id}`);
      toast.success('Deleted'); refresh();
    } catch (e) { toast.error(e.message); }
  };

  const classLookup = Object.fromEntries((data.share_classes || []).map(c => [c.id, c]));
  const holderLookup = Object.fromEntries((data.shareholders || []).map(s => [s.id, s]));
  const noClassesOrHolders = (data.share_classes || []).length === 0 || (data.shareholders || []).length === 0;

  return (
    <div className="space-y-3" data-testid="cap-holdings-tab">
      {noClassesOrHolders && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Add at least one share class and one shareholder before creating holdings.
        </div>
      )}
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={noClassesOrHolders}
          data-testid="add-holding-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Holding
        </Button>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={form.shareholder_id} onValueChange={(v) => setForm({ ...form, shareholder_id: v })}>
              <SelectTrigger data-testid="h-shareholder-select"><SelectValue placeholder="Shareholder" /></SelectTrigger>
              <SelectContent>
                {(data.shareholders || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.share_class_id} onValueChange={(v) => setForm({ ...form, share_class_id: v })}>
              <SelectTrigger data-testid="h-class-select"><SelectValue placeholder="Share class" /></SelectTrigger>
              <SelectContent>
                {(data.share_classes || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Shares" value={form.shares}
              onChange={(e) => setForm({ ...form, shares: e.target.value })} data-testid="h-shares-input" />
            <Input type="number" placeholder="Amount paid (₹)" value={form.amount_paid}
              onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} data-testid="h-amount-input" />
            <Input type="date" placeholder="As-of date" value={form.as_of_date}
              onChange={(e) => setForm({ ...form, as_of_date: e.target.value })} data-testid="h-date-input" />
            <Input placeholder="Certificate no." value={form.certificate_no}
              onChange={(e) => setForm({ ...form, certificate_no: e.target.value })} data-testid="h-cert-input" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setForm(init); setOpen(false); }}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={submit}
              disabled={saving || !form.shareholder_id || !form.share_class_id || !form.shares}
              data-testid="h-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        </div>
      )}
      {(data.holdings || []).length === 0 ? (
        <p className="text-xs text-slate-400 italic">No holdings yet.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Shareholder</th>
                <th className="text-left px-3 py-2">Class</th>
                <th className="text-right px-3 py-2">Shares</th>
                <th className="text-right px-3 py-2">Amount Paid</th>
                <th className="text-left px-3 py-2">As-of</th>
                <th className="text-left px-3 py-2">Cert No.</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.holdings.map((h) => (
                <tr key={h.id} className="border-t border-slate-100" data-testid={`h-row-${h.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{holderLookup[h.shareholder_id]?.name || h.shareholder_id}</td>
                  <td className="px-3 py-2 text-slate-600">{classLookup[h.share_class_id]?.name || h.share_class_id}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(h.shares)}</td>
                  <td className="px-3 py-2 text-right font-mono">₹{fmtNum(h.amount_paid)}</td>
                  <td className="px-3 py-2 text-slate-600">{h.as_of_date || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{h.certificate_no || '—'}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => remove(h.id)} className="text-rose-500 hover:text-rose-700" data-testid={`h-delete-${h.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── ALLOTMENTS ───────────────────────
function AllotmentsTab({ data, pas3Filings, clientId, refresh }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const init = { date: '', share_class_id: '', shares: '', issue_price_per_share: '',
                 consideration_type: 'cash', linked_pas3_form: '', pas3_filing_date: '', notes: '' };
  const [form, setForm] = useState(init);

  // De-dupe PAS-3 filings by (form_name + date_of_filing) so the picker
  // doesn't show 5 rows for the same day (MCA exports repeat attachments).
  const pas3Options = [];
  {
    const seen = new Set();
    for (const p of (pas3Filings || [])) {
      const key = `${p.form_name}::${p.date_of_filing}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pas3Options.push(p);
    }
  }

  const pickPas3 = (dateOfFiling) => {
    const opt = pas3Options.find((p) => p.date_of_filing === dateOfFiling);
    if (!opt) return;
    setForm({
      ...form,
      linked_pas3_form: opt.form_name || 'PAS-3',
      pas3_filing_date: opt.date_of_filing || form.pas3_filing_date,
    });
    toast.success(`Linked to PAS-3 filed on ${opt.date_of_filing}`);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await apiCall('POST', `${clientId}/shareholding/allotments`, {
        date: form.date,
        share_class_id: form.share_class_id,
        shares: parseFloat(form.shares),
        issue_price_per_share: form.issue_price_per_share ? parseFloat(form.issue_price_per_share) : 0,
        consideration_type: form.consideration_type,
        linked_pas3_form: form.linked_pas3_form || '',
        pas3_filing_date: form.pas3_filing_date || null,
        notes: form.notes || '',
      });
      toast.success('Allotment recorded');
      setForm(init); setOpen(false); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this allotment record?')) return;
    try {
      await apiCall('DELETE', `${clientId}/shareholding/allotments/${id}`);
      toast.success('Deleted'); refresh();
    } catch (e) { toast.error(e.message); }
  };

  const classLookup = Object.fromEntries((data.share_classes || []).map(c => [c.id, c]));
  const noClasses = (data.share_classes || []).length === 0;

  return (
    <div className="space-y-3" data-testid="cap-allotments-tab">
      {noClasses && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Add at least one share class before logging allotments.
        </div>
      )}
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={noClasses}
          data-testid="add-allotment-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Allotment
        </Button>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input type="date" placeholder="Allotment date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="a-date-input" />
            <Select value={form.share_class_id} onValueChange={(v) => setForm({ ...form, share_class_id: v })}>
              <SelectTrigger data-testid="a-class-select"><SelectValue placeholder="Share class" /></SelectTrigger>
              <SelectContent>
                {(data.share_classes || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Shares allotted" value={form.shares}
              onChange={(e) => setForm({ ...form, shares: e.target.value })} data-testid="a-shares-input" />
            <Input type="number" placeholder="Issue price / share (₹)" value={form.issue_price_per_share}
              onChange={(e) => setForm({ ...form, issue_price_per_share: e.target.value })} data-testid="a-price-input" />
            <Select value={form.consideration_type} onValueChange={(v) => setForm({ ...form, consideration_type: v })}>
              <SelectTrigger data-testid="a-cons-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONSIDERATION_TYPES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="a-notes-input" />
          </div>

          {/* Cross-link to Filing Index — the fix for the reported miss */}
          <div className="border border-emerald-100 bg-emerald-50/60 rounded p-2 space-y-2" data-testid="pas3-linker-box">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase text-emerald-700">
                Link to filed PAS-3 (from MCA Filing Index)
              </label>
              {pas3Options.length > 0 && (
                <span className="text-[10px] text-emerald-700">
                  {pas3Options.length} PAS-3 filing{pas3Options.length !== 1 ? 's' : ''} found
                </span>
              )}
            </div>
            {pas3Options.length === 0 ? (
              <p className="text-[11px] text-slate-500 italic">
                No PAS-3 filings detected in the MCA Filing Index. Upload the
                Filing Index above or enter the SRN manually below.
              </p>
            ) : (
              <Select value={form.pas3_filing_date || ''} onValueChange={pickPas3}>
                <SelectTrigger data-testid="a-pas3-picker-select">
                  <SelectValue placeholder="Pick a PAS-3 filing to link…" />
                </SelectTrigger>
                <SelectContent>
                  {pas3Options.map((p) => (
                    <SelectItem key={`${p.form_name}::${p.date_of_filing}`}
                      value={p.date_of_filing || 'none'}
                      data-testid={`a-pas3-option-${p.date_of_filing}`}>
                      {p.form_name} · filed {p.date_of_filing} · {p.description || 'Form'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input placeholder="PAS-3 SRN (manual override)" value={form.linked_pas3_form}
                onChange={(e) => setForm({ ...form, linked_pas3_form: e.target.value })} data-testid="a-pas3-input" />
              <Input type="date" placeholder="PAS-3 filing date" value={form.pas3_filing_date}
                onChange={(e) => setForm({ ...form, pas3_filing_date: e.target.value })} data-testid="a-pas3date-input" />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setForm(init); setOpen(false); }}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={submit}
              disabled={saving || !form.date || !form.share_class_id || !form.shares}
              data-testid="a-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        </div>
      )}
      {(data.allotments || []).length === 0 ? (
        <p className="text-xs text-slate-400 italic">No allotments logged yet.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Class</th>
                <th className="text-right px-3 py-2">Shares</th>
                <th className="text-right px-3 py-2">Issue ₹</th>
                <th className="text-left px-3 py-2">Consideration</th>
                <th className="text-left px-3 py-2">PAS-3 SRN</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.allotments.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`a-row-${a.id}`}>
                  <td className="px-3 py-2 font-mono text-slate-700">{a.date}</td>
                  <td className="px-3 py-2 text-slate-600">{classLookup[a.share_class_id]?.name || a.share_class_id}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(a.shares)}</td>
                  <td className="px-3 py-2 text-right font-mono">₹{fmtNum(a.issue_price_per_share)}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{a.consideration_type}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-600">{a.linked_pas3_form || '—'}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => remove(a.id)} className="text-rose-500 hover:text-rose-700" data-testid={`a-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── ESOP POOL ───────────────────────
function EsopBlock({ pool, clientId, refresh }) {
  const empty = { total_pool_size: '', vested_options: '', unvested_options: '', exercised_options: '', scheme_name: '', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const startEdit = () => {
    setForm(pool ? {
      total_pool_size: pool.total_pool_size || '',
      vested_options: pool.vested_options || '',
      unvested_options: pool.unvested_options || '',
      exercised_options: pool.exercised_options || '',
      scheme_name: pool.scheme_name || '',
      notes: pool.notes || '',
    } : empty);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiCall('PUT', `${clientId}/shareholding/esop-pool`, {
        total_pool_size: parseFloat(form.total_pool_size) || 0,
        vested_options: parseFloat(form.vested_options) || 0,
        unvested_options: parseFloat(form.unvested_options) || 0,
        exercised_options: parseFloat(form.exercised_options) || 0,
        scheme_name: form.scheme_name,
        notes: form.notes,
      });
      toast.success('ESOP pool saved');
      setEditing(false); refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const clearPool = async () => {
    if (!window.confirm('Clear the ESOP pool block?')) return;
    try {
      await apiCall('PUT', `${clientId}/shareholding/esop-pool`, { clear: true });
      toast.success('Cleared'); refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="border border-slate-200 rounded p-3" data-testid="esop-block">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">ESOP Pool</h4>
        <div className="flex gap-1">
          {pool && !editing && (
            <>
              <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={startEdit} data-testid="esop-edit-btn">Edit</Button>
              <Button size="sm" variant="ghost" className="h-7 text-[10px] text-rose-500" onClick={clearPool} data-testid="esop-clear-btn">Clear</Button>
            </>
          )}
          {!pool && !editing && (
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={startEdit} data-testid="esop-add-btn">
              <Plus className="w-3 h-3 mr-1" /> Set ESOP Pool
            </Button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="Scheme name" value={form.scheme_name}
              onChange={(e) => setForm({ ...form, scheme_name: e.target.value })} data-testid="esop-scheme-input" />
            <Input type="number" placeholder="Total pool size" value={form.total_pool_size}
              onChange={(e) => setForm({ ...form, total_pool_size: e.target.value })} data-testid="esop-total-input" />
            <Input type="number" placeholder="Vested" value={form.vested_options}
              onChange={(e) => setForm({ ...form, vested_options: e.target.value })} data-testid="esop-vested-input" />
            <Input type="number" placeholder="Unvested" value={form.unvested_options}
              onChange={(e) => setForm({ ...form, unvested_options: e.target.value })} data-testid="esop-unvested-input" />
            <Input type="number" placeholder="Exercised" value={form.exercised_options}
              onChange={(e) => setForm({ ...form, exercised_options: e.target.value })} data-testid="esop-exercised-input" />
            <Input placeholder="Notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="esop-notes-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="w-3 h-3 mr-1" /> Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="esop-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
            </Button>
          </div>
        </div>
      ) : pool ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
          <Stat label="Scheme" value={pool.scheme_name || '—'} />
          <Stat label="Pool size" value={fmtNum(pool.total_pool_size)} />
          <Stat label="Vested" value={fmtNum(pool.vested_options)} />
          <Stat label="Unvested" value={fmtNum(pool.unvested_options)} />
          <Stat label="Exercised" value={fmtNum(pool.exercised_options)} />
          {pool.notes && <div className="col-span-full text-slate-500 text-[11px]">Note: {pool.notes}</div>}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">No ESOP pool configured.</p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
      <div className="text-sm font-semibold text-slate-800 font-mono">{value}</div>
    </div>
  );
}

// ─────────────────────── MAIN PANEL ───────────────────────
export default function CapTablePanel({ client, activeSection, setActiveSection }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('share-classes');
  const sectionKey = `captable-${client.id}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/clients/${client.id}/shareholding`, {
        headers: authHeader(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load cap table');
      setView(data);
    } catch (e) {
      toast.error(e.message);
      setView({ shareholding: { share_classes: [], shareholders: [], holdings: [], allotments: [], esop_pool: null }, summary: { counts: {} } });
    } finally { setLoading(false); }
  }, [client.id]);

  useEffect(() => {
    if (activeSection === sectionKey && !view) refresh();
  }, [activeSection, sectionKey, view, refresh]);

  const summary = view?.summary || { counts: {} };
  const sh = view?.shareholding || { share_classes: [], shareholders: [], holdings: [], allotments: [], esop_pool: null };
  const totalEquity = summary.total_equity_shares || 0;
  const fdb = summary.fully_diluted || { fdb_total: 0, rows: [], esop_pool_in_fdb: 0 };
  const pas3Audit = summary.pas3_audit || [];
  const stale = summary.stale || { is_stale: false };
  const pas3Missing = pas3Audit.filter((a) => a.status === 'missing').length;

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/api/clients/${client.id}/shareholding/export.csv`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error('CSV download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CapTable_${(client.entity_name || client.id).replace(/[^a-zA-Z0-9-_]/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Cap table CSV downloaded');
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <CollapsibleSection
      icon={PieChart}
      title="Cap Table"
      borderColor="border-slate-200"
      iconColor="text-slate-500"
      bgHover="hover:bg-slate-50/50"
      chevronBg="bg-slate-200"
      sectionKey={sectionKey}
      testId={sectionKey}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      badge={view ? (
        <>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
            {summary.counts?.share_classes || 0} classes
          </span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
            {summary.counts?.shareholders || 0} holders
          </span>
          {totalEquity > 0 && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              {fmtNum(totalEquity)} equity shares
            </span>
          )}
          {fdb.fdb_total > 0 && fdb.fdb_total !== totalEquity && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid="cap-fdb-badge">
              {fmtNum(fdb.fdb_total)} FDB
            </span>
          )}
          {pas3Missing > 0 && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="cap-pas3-missing-badge">
              {pas3Missing} PAS-3 missing
            </span>
          )}
          {stale.is_stale && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="cap-stale-badge">
              Stale · {stale.days_since_update}d
            </span>
          )}
        </>
      ) : null}
      headerRight={view && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1"
            onClick={(e) => { e.stopPropagation(); downloadCsv(); }}
            disabled={loading || (sh.holdings?.length || 0) === 0}
            data-testid="cap-csv-btn">
            <Download className="w-3 h-3" /> CSV
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1"
            onClick={(e) => { e.stopPropagation(); refresh(); }}
            disabled={loading} data-testid="cap-refresh-btn">
            <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      )}
    >
      {loading && !view ? (
        <div className="p-4 text-xs text-slate-400">Loading cap table…</div>
      ) : (
        <div className="p-3 space-y-4">
          {/* Stale-cap-table banner */}
          {stale.is_stale && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="stale-banner">
              <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Cap table may be out of date.</strong> Last edit was {stale.days_since_update} day(s) ago (threshold: {stale.threshold_days}d). Reach out to the founders to confirm there have been no new allotments, transfers, or ESOP grants.
              </div>
            </div>
          )}

          {/* Quick equity ownership summary */}
          {summary.ownership_equity?.length > 0 && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">
                Equity Ownership Snapshot
                <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">
                  ({fmtNum(totalEquity)} equity shares)
                </span>
              </h4>
              <div className="space-y-1">
                {summary.ownership_equity.map((o) => (
                  <div key={o.shareholder_id} className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-slate-800 min-w-[140px]">{o.name}</span>
                    <span className="text-[10px] uppercase font-mono px-1 py-0.5 rounded bg-violet-50 text-violet-700">{o.kind}</span>
                    <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden">
                      <div className="h-full bg-indigo-500" style={{ width: `${o.pct}%` }} />
                    </div>
                    <span className="font-mono text-slate-700 min-w-[70px] text-right">{fmtNum(o.shares)}</span>
                    <span className="font-mono font-semibold text-slate-800 min-w-[60px] text-right">{o.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fully-Diluted snapshot */}
          {fdb.fdb_total > 0 && (
            <div className="border border-sky-200 rounded p-3 bg-sky-50/30" data-testid="fdb-block">
              <h4 className="text-xs font-bold uppercase tracking-wide text-sky-700 mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                Fully-Diluted Basis
                <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">
                  ({fmtNum(fdb.fdb_total)} total · ESOP {fmtNum(fdb.esop_pool_in_fdb)} included)
                </span>
              </h4>
              <div className="space-y-1">
                {fdb.rows.map((r) => (
                  <div key={r.shareholder_id} className="flex items-center gap-2 text-xs" data-testid={`fdb-row-${r.shareholder_id}`}>
                    <span className="font-medium text-slate-800 min-w-[140px] truncate">{r.name}</span>
                    <span className={`text-[10px] uppercase font-mono px-1 py-0.5 rounded ${r.kind === 'esop_pool' ? 'bg-rose-50 text-rose-700' : 'bg-violet-50 text-violet-700'}`}>
                      {r.kind}
                    </span>
                    <div className="flex-1 h-2 bg-sky-100 rounded overflow-hidden">
                      <div className={`h-full ${r.kind === 'esop_pool' ? 'bg-rose-400' : 'bg-sky-500'}`} style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="font-mono text-slate-700 min-w-[70px] text-right">{fmtNum(r.equity_equivalent)}</span>
                    <span className="font-mono font-semibold text-slate-800 min-w-[60px] text-right">{r.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PAS-3 linkage audit */}
          {pas3Audit.length > 0 && (
            <div className="border border-slate-200 rounded p-3" data-testid="pas3-audit-block">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2 flex items-center gap-1.5">
                PAS-3 Linkage Audit
                {pas3Missing > 0 ? (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                    {pas3Missing} missing
                  </span>
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                )}
              </h4>
              <div className="divide-y divide-slate-100 text-xs">
                {pas3Audit.map((a) => {
                  const stylesByStatus = {
                    linked_manual:    { color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'LINKED' },
                    matched_auto:     { color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'MATCHED' },
                    missing:          { color: 'text-rose-700',    bg: 'bg-rose-50',    label: 'MISSING' },
                    no_filing_index:  { color: 'text-slate-500',   bg: 'bg-slate-100',  label: 'NO INDEX' },
                  };
                  const st = stylesByStatus[a.status] || stylesByStatus.missing;
                  return (
                    <div key={a.allotment_id} className="py-1.5 flex items-center gap-2" data-testid={`pas3-row-${a.allotment_id}`}>
                      <span className="font-mono text-slate-500 min-w-[90px]">{a.date || '—'}</span>
                      <span className="font-mono text-slate-700 min-w-[100px]">{fmtNum(a.shares)} shares</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${st.bg} ${st.color} min-w-[70px] text-center`}>
                        {st.label}
                      </span>
                      <span className="text-slate-600 flex-1 truncate" title={a.reason}>{a.reason}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-4 h-9" data-testid="cap-tabs">
              <TabsTrigger value="share-classes" className="text-xs" data-testid="cap-tab-classes">
                Share Classes ({sh.share_classes.length})
              </TabsTrigger>
              <TabsTrigger value="shareholders" className="text-xs" data-testid="cap-tab-shareholders">
                Shareholders ({sh.shareholders.length})
              </TabsTrigger>
              <TabsTrigger value="holdings" className="text-xs" data-testid="cap-tab-holdings">
                Holdings ({sh.holdings.length})
              </TabsTrigger>
              <TabsTrigger value="allotments" className="text-xs" data-testid="cap-tab-allotments">
                Allotments ({sh.allotments.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="share-classes" className="mt-3">
              <ShareClassesTab data={sh} clientId={client.id} refresh={refresh} />
            </TabsContent>
            <TabsContent value="shareholders" className="mt-3">
              <ShareholdersTab data={sh} mcaDirectors={view?.mca_directors} clientId={client.id} refresh={refresh} />
            </TabsContent>
            <TabsContent value="holdings" className="mt-3">
              <HoldingsTab data={sh} clientId={client.id} refresh={refresh} />
            </TabsContent>
            <TabsContent value="allotments" className="mt-3">
              <AllotmentsTab data={sh} pas3Filings={summary.pas3_filings || []} clientId={client.id} refresh={refresh} />
            </TabsContent>
          </Tabs>

          {/* ESOP block */}
          <EsopBlock pool={sh.esop_pool} clientId={client.id} refresh={refresh} />
        </div>
      )}
    </CollapsibleSection>
  );
}
